import type { ChatComposerHandle } from "./ChatComposerHandle";
import { ThreadComposerLayout } from "./ThreadComposerLayout";
import { RemoteThreadNotices } from "./RemoteThreadNotices";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type {
  RemoteThreadDispatchSettings,
  RemoteThreadEnvironmentWaitingReason,
} from "./useRemoteThreadExecutionIntent";
import { useMemo, useRef, type ReactElement, type RefObject } from "react";
import type { LegendListRef } from "@legendapp/list/react";
import type {
  CmmdRuntimeThreadReplica,
  EnvironmentId,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";
import type { TimestampFormat } from "@cmmd-forge/contracts/settings";

import { useSettings } from "../../hooks/useSettings";
import { useComposerThreadDraft } from "../../composerDraftStore";
import { useTheme } from "../../hooks/useTheme";
import { MessagesTimeline } from "./MessagesTimeline";
import {
  RemoteRuntimeThreadComposer,
  resolveRemoteComposerProvider,
} from "./RemoteRuntimeThreadComposer";
import { useRemoteSubmissionReveal } from "./useRemoteSubmissionReveal";
import { RemoteThreadSubmissionStatus } from "./RemoteThreadSubmissionStatus";
import { RemoteThreadError, RemoteThreadLoading } from "./RemoteRuntimeThreadStates";
import { deriveTimelineEntriesFromReplica } from "./remoteRuntimeTimelineEntries";
import { RateLimitBanner } from "./RateLimitBanner";
import { currentRemoteUsageLimitError } from "./remoteUsageLimitRecovery";
import { stagedComposerSubmissionToChatMessage } from "./stagedComposerSubmissionMessage";
import type { ComposerBannerStackItem } from "./composer-notices/ComposerBannerStack";
import { useComposerModelSelection } from "./useComposerModelSelection";
import { useRemoteComposerUsageNotice } from "./composer-notices/useRemoteComposerUsageNotice";
import { useThreadCmmdProjectId } from "../../lib/threadCmmdProjectId";
import { resolveEnvironmentHttpUrl } from "../../environments/runtime/catalog";
import { DEFAULT_INTERACTION_MODE, DEFAULT_RUNTIME_MODE } from "../../types";
import type { RemoteRuntimeConnectionState } from "./remoteRuntimeThreadConnection.logic";
import type { RemoteThreadComposerContext } from "./remoteThreadComposerContext";

export type RemoteRuntimeThreadViewState =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string }
  | {
      readonly kind: "ready";
      readonly replica: CmmdRuntimeThreadReplica;
      /**
       * Present when this transcript was RETAINED through a failed refresh
       * rather than freshly read. The history is still the last authorized
       * copy, but it is not known to be current, so it must be labelled rather
       * than rendered as if it just loaded.
       */
      readonly staleMessage?: string;
    };

export interface RemoteRuntimeThreadViewProps {
  readonly composerContext: RemoteThreadComposerContext;
  readonly state: RemoteRuntimeThreadViewState;
  readonly connectionState: RemoteRuntimeConnectionState;
  readonly environmentId: EnvironmentId;
  readonly runtimeId?: string;
  readonly threadRef: ScopedThreadRef;
  readonly fallbackTitle: string;
  readonly fallbackRuntimeName: string;
  readonly historyOnly: boolean;
  /**
   * Why the Execution Intent is waiting on the Environment, from CMMD's typed
   * refusal. A waiting reason is held work, not a failed send: the Environment
   * Notice reports it and the "Not sent" strip stays out of the way.
   */
  readonly environmentWaitingReason?: RemoteThreadEnvironmentWaitingReason | null;
  readonly onExecutionIntent: (settings?: RemoteThreadDispatchSettings) => void;
  readonly onRetry: () => void;
  readonly onRefreshHistory?: (() => void) | undefined;
}

const NO_OP = () => {};
const EMPTY_MESSAGE_ID_MAP = new Map<never, never>();

export function RemoteRuntimeThreadView(props: RemoteRuntimeThreadViewProps) {
  const listRef = useRef<LegendListRef | null>(null);
  const composerRef = useRef<ChatComposerHandle | null>(null);
  const isAtEndRef = useRef(true);
  const onIsAtEndChange = (isAtEnd: boolean) =>
    updateRemoteTimelineEnd(isAtEndRef, composerRef, isAtEnd);
  const settings = useSettings();
  const timestampFormat = settings.timestampFormat;
  const { resolvedTheme } = useTheme();
  const draft = useComposerThreadDraft(props.threadRef);
  const composerModelSelection = useComposerModelSelection({
    threadRef: props.threadRef,
    providers: props.composerContext.providers,
    resolvedProvider: resolveRemoteComposerProvider(props.composerContext, draft.activeProvider),
    lockedProvider: props.composerContext.lockedProvider ?? null,
    threadModelSelection: props.composerContext.modelSelection,
    projectModelSelection: null,
    settings,
  });
  const replica = props.state.kind === "ready" ? props.state.replica : null;
  const usageNotice = useRemoteComposerUsageNotice({
    threadId: props.threadRef.threadId,
    modelSelection: composerModelSelection.modelSelection,
    replica,
    pendingInputId: draft.stagedSubmission?.messageId ?? null,
  });
  const environmentWaitingReason = props.environmentWaitingReason ?? null;
  const { pending, failure, showSubmissionStatus } = remoteSubmissionState(
    props.connectionState,
    draft.stagedSubmission,
    environmentWaitingReason,
  );

  const timelineEntries = useMemo(
    () => remoteTimelineEntries(replica, pending, props.environmentId),
    [replica, pending, props.environmentId],
  );

  const onExecutionIntent = useRemoteSubmissionReveal(
    scopedThreadKey(props.threadRef),
    pending?.messageId,
    props.state.kind,
    listRef,
    props.onExecutionIntent,
  );
  const banner = (
    <RemoteRuntimeThreadBanner
      props={props}
      replica={replica}
      hasSubmission={pending !== null}
      environmentWaitingReason={environmentWaitingReason}
      {...remoteDraftModes(draft)}
      usageNotice={usageNotice.item}
    />
  );

  return (
    <RemoteRuntimeThreadContent
      props={props}
      listRef={listRef}
      composerRef={composerRef}
      isAtEndRef={isAtEndRef}
      composerModelSelection={composerModelSelection}
      banner={banner}
      onOpenUsageLimits={usageNotice.open}
      timelineEntries={timelineEntries}
      onExecutionIntent={onExecutionIntent}
      showSubmissionStatus={showSubmissionStatus}
      failure={failure}
      resolvedTheme={resolvedTheme}
      timestampFormat={timestampFormat}
      replica={replica}
      onIsAtEndChange={onIsAtEndChange}
    />
  );
}

/**
 * The modes a turn would run with, taking the thread Draft's choice when it has
 * one. Kept beside the Draft read so the view body states one fact per line.
 */
function remoteDraftModes(draft: ReturnType<typeof useComposerThreadDraft>) {
  return {
    runtimeMode: draft.runtimeMode ?? DEFAULT_RUNTIME_MODE,
    interactionMode: draft.interactionMode ?? DEFAULT_INTERACTION_MODE,
  };
}

function RemoteRuntimeThreadContent(input: {
  readonly props: RemoteRuntimeThreadViewProps;
  readonly listRef: RefObject<LegendListRef | null>;
  readonly composerRef: RefObject<ChatComposerHandle | null>;
  readonly isAtEndRef: RefObject<boolean>;
  readonly composerModelSelection: ReturnType<typeof useComposerModelSelection>;
  readonly banner: ReactElement;
  readonly onOpenUsageLimits: () => boolean;
  readonly timelineEntries: ReturnType<typeof remoteTimelineEntries>;
  readonly onExecutionIntent: (settings?: RemoteThreadDispatchSettings) => void;
  readonly showSubmissionStatus: boolean;
  readonly failure: string | null;
  readonly resolvedTheme: "light" | "dark";
  readonly timestampFormat: TimestampFormat;
  readonly replica: CmmdRuntimeThreadReplica | null;
  readonly onIsAtEndChange: (isAtEnd: boolean) => void;
}) {
  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col bg-background text-foreground">
      <ThreadComposerLayout
        key={scopedThreadKey(input.props.threadRef)}
        composer={
          <>
            {input.showSubmissionStatus ? (
              <RemoteThreadSubmissionStatus failure={input.failure} onRetry={input.props.onRetry} />
            ) : null}
            <RemoteRuntimeThreadComposer
              composerRef={input.composerRef}
              listRef={input.listRef}
              isAtEndRef={input.isAtEndRef}
              context={input.props.composerContext}
              replica={input.replica}
              modelSelection={input.composerModelSelection}
              banner={input.banner}
              connectionState={input.props.connectionState}
              historyOnly={input.props.historyOnly}
              onExecutionIntent={input.onExecutionIntent}
              onOpenUsageLimits={input.onOpenUsageLimits}
              resolvedTheme={input.resolvedTheme}
              threadRef={input.props.threadRef}
              {...(input.props.runtimeId ? { runtimeId: input.props.runtimeId } : {})}
            />
          </>
        }
      >
        <RemoteRuntimeThreadBody
          state={input.props.state}
          historyOnly={input.props.historyOnly}
          onRetry={input.props.onRetry}
          listRef={input.listRef}
          timelineEntries={input.timelineEntries}
          threadRef={input.props.threadRef}
          environmentId={input.props.environmentId}
          resolvedTheme={input.resolvedTheme}
          timestampFormat={input.timestampFormat}
          onIsAtEndChange={input.onIsAtEndChange}
          replica={input.replica}
        />
      </ThreadComposerLayout>
    </div>
  );
}

function RemoteRuntimeThreadBody(input: {
  readonly state: RemoteRuntimeThreadViewState;
  readonly historyOnly: boolean;
  readonly onRetry: () => void;
  readonly listRef: RefObject<LegendListRef | null>;
  readonly timelineEntries: ReturnType<typeof remoteTimelineEntries>;
  readonly threadRef: ScopedThreadRef;
  readonly environmentId: EnvironmentId;
  readonly resolvedTheme: "light" | "dark";
  readonly timestampFormat: TimestampFormat;
  readonly onIsAtEndChange: (isAtEnd: boolean) => void;
  readonly replica: CmmdRuntimeThreadReplica | null;
}) {
  return input.state.kind === "error" ? (
    <RemoteThreadError message={input.state.message} onRetry={input.onRetry} />
  ) : input.state.kind === "loading" ? (
    <RemoteThreadLoading historyOnly={input.historyOnly} />
  ) : (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <MessagesTimeline
        key={scopedThreadKey(input.threadRef)}
        isWorking={false}
        activeTurnInProgress={false}
        activeTurnId={null}
        activeTurnStartedAt={null}
        latestTurn={null}
        runningTurnId={null}
        listRef={input.listRef}
        timelineEntries={input.timelineEntries}
        turnDiffSummaryByAssistantMessageId={EMPTY_MESSAGE_ID_MAP}
        routeThreadKey={scopedThreadKey(input.threadRef)}
        onOpenTurnDiff={NO_OP}
        onRevertUserMessage={NO_OP}
        isRevertingCheckpoint={false}
        onImageExpand={NO_OP}
        activeThreadEnvironmentId={input.environmentId}
        markdownCwd={undefined}
        providerSkills={[]}
        resolvedTheme={input.resolvedTheme}
        timestampFormat={input.timestampFormat}
        workspaceRoot={undefined}
        onIsAtEndChange={input.onIsAtEndChange}
        emptyStateProjectName={input.replica?.project.title ?? null}
      />
    </div>
  );
}

function updateRemoteTimelineEnd(
  isAtEndRef: RefObject<boolean>,
  composerRef: RefObject<ChatComposerHandle | null>,
  isAtEnd: boolean,
): void {
  const wasAtEnd = isAtEndRef.current;
  isAtEndRef.current = isAtEnd;
  if (isAtEnd && !wasAtEnd) composerRef.current?.restoreAfterTimelineReachedEnd();
}

function RemoteRuntimeThreadBanner(input: {
  readonly props: RemoteRuntimeThreadViewProps;
  readonly replica: CmmdRuntimeThreadReplica | null;
  readonly hasSubmission: boolean;
  readonly environmentWaitingReason: RemoteThreadEnvironmentWaitingReason | null;
  readonly runtimeMode: NonNullable<ReturnType<typeof useComposerThreadDraft>["runtimeMode"]>;
  readonly interactionMode: NonNullable<
    ReturnType<typeof useComposerThreadDraft>["interactionMode"]
  >;
  readonly usageNotice: ComposerBannerStackItem | null;
}) {
  const usageLimitError = currentRemoteUsageLimitError(input.replica);
  const modelSelection = input.props.composerContext.modelSelection;
  const projectId = useThreadCmmdProjectId(input.props.threadRef);
  return (
    <>
      {usageLimitError && modelSelection ? (
        <RateLimitBanner
          environmentId={input.props.environmentId}
          projectId={projectId}
          {...(input.props.runtimeId ? { runtimeId: input.props.runtimeId } : {})}
          threadId={input.props.threadRef.threadId}
          modelSelection={modelSelection}
          runtimeMode={input.runtimeMode}
          interactionMode={input.interactionMode}
          rateLimitStatus={undefined}
          threadError={usageLimitError}
          {...(input.props.onRefreshHistory ? { onResumed: input.props.onRefreshHistory } : {})}
        />
      ) : null}
      <RemoteThreadNotices
        hasSubmission={input.hasSubmission}
        environmentWaitingReason={input.environmentWaitingReason}
        onRefreshHistory={input.props.onRefreshHistory}
        connectionState={input.props.connectionState}
        runtimeName={input.props.fallbackRuntimeName}
        staleMessage={remoteStaleMessage(input.props.state)}
        onRetry={input.props.onRetry}
        usageNotice={input.usageNotice}
      />
    </>
  );
}

function remoteTimelineEntries(
  replica: CmmdRuntimeThreadReplica | null,
  pending: ReturnType<typeof useComposerThreadDraft>["stagedSubmission"],
  environmentId: EnvironmentId,
) {
  const entries = deriveTimelineEntriesFromReplica(replica?.events ?? [], (attachmentId) =>
    resolveEnvironmentHttpUrl({
      environmentId,
      pathname: `/attachments/${encodeURIComponent(attachmentId)}`,
    }),
  );
  const stagedMessage = stagedComposerSubmissionToChatMessage(pending);
  if (
    stagedMessage &&
    !entries.some(
      (entry) => entry.id === stagedMessage.id || entry.id === `message:${stagedMessage.id}`,
    )
  ) {
    entries.push({
      id: stagedMessage.id,
      kind: "message",
      createdAt: stagedMessage.createdAt,
      message: stagedMessage,
    });
  }
  return entries;
}

/**
 * Splits the staged submission into what the thread renders below the
 * transcript. A waiting Environment owns its own notice, so the status strip
 * stays silent rather than saying "Preparing..." over an explained wait.
 */
function remoteSubmissionState(
  connection: RemoteRuntimeThreadViewProps["connectionState"],
  submission: ReturnType<typeof useComposerThreadDraft>["stagedSubmission"],
  waitingReason: RemoteThreadEnvironmentWaitingReason | null,
) {
  const failure = connection.kind === "failed" ? connection.message : null;
  const showSubmissionStatus =
    submission !== null &&
    waitingReason === null &&
    (connection.kind === "connecting" ||
      connection.kind === "retrying" ||
      connection.kind === "failed");
  return { pending: submission, failure, showSubmissionStatus };
}

function remoteStaleMessage(state: RemoteRuntimeThreadViewState): string | undefined {
  return state.kind === "ready" ? state.staleMessage : undefined;
}
